import React from "react";
import { Box, Avatar, CircularProgress, Tooltip } from "@mui/joy";
import { SAddPhoto, SDownload } from "../../../../../styles/StyledMUI";
import type { ZekayCustomer } from "../../../../../api/customers/Customer.typed";
import { saveAs } from "../../../../../utils/fileSaver";
import { useCustomer } from "../../../../../contexts/api";
import { useAlert } from "../../../../../contexts/app/notificationToast";
import IconButtonCTA from "../../../../common/IconButtonCTA";

import empty from "../../../../../styles/images/noimage.png";

interface PhotoComponentProps {
  customer: ZekayCustomer | undefined;
  setCustomerUpdated: React.Dispatch<React.SetStateAction<ZekayCustomer | undefined>>;
  readOnly?: boolean;
}

export default function PhotoComponent({
  customer,
  setCustomerUpdated,
  readOnly = false,
}: PhotoComponentProps) {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isAvatarLoading, setIsAvatarLoading] = React.useState<boolean>(true);
  const { getCustomer, updateFile } = useCustomer();
  const { showAlert } = useAlert();
  const [photoBustToken, setPhotoBustToken] = React.useState<number>(0);

  const uploadImage = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsLoading(true);
      const file = event.target.files?.[0];
      if (!file) {
        setIsLoading(false);
        return;
      }

      // Validation: only JPG/JPEG allowed
      const isJpegMime = /^image\/jpeg$/i.test(file.type);
      const isJpegExt = /\.jpe?g$/i.test(file.name);
      if (!isJpegMime && !isJpegExt) {
        showAlert("Seuls les fichiers JPG/JPEG sont autorisés", "warning");
        setIsLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("CustomerID", String(customer?.Id));
      formData.append("Photo", file);

      await updateFile(formData)
        .then(() =>
          setTimeout(
            () =>
              customer?.Id != null
                ? getCustomer(String(customer.Id)).then((r) => {
                    setCustomerUpdated(r);
                    setPhotoBustToken(Date.now());
                  })
                : Promise.resolve(),
            1000,
          ),
        )
        .finally(() => setIsLoading(false));
    },
    [customer?.Id, getCustomer, setCustomerUpdated, showAlert, updateFile],
  );

  const downloadImage = React.useCallback(() => {
    if (typeof customer?.Photo === "string") {
      if (customer.Photo.startsWith("data:")) {
        saveAs(customer.Photo, `${customer.FirstName}_${customer.LastName}`);
      } else {
        window.open(customer.Photo, "_blank");
      }
    }
  }, [customer?.FirstName, customer?.LastName, customer?.Photo]);

  // Build image URL with cache-busting query to avoid stale memory-cached images
  const customerUpdatedAt = (customer as any)?.UpdatedAt;
  const avatarSrc = React.useMemo(() => {
    const src = typeof customer?.Photo === "string" ? customer.Photo : empty;
    if (!src || src.startsWith("data:")) return src;
    const hasQuery = src.includes("?");
    const token = customer && customerUpdatedAt ? customerUpdatedAt : photoBustToken;
    return token ? `${src}${hasQuery ? "&" : "?"}v=${encodeURIComponent(String(token))}` : src;
  }, [customer, customerUpdatedAt, photoBustToken]);

  React.useEffect(() => {
    setIsAvatarLoading(true);
  }, [avatarSrc]);

  const handleAvatarLoaded = React.useCallback(() => {
    setIsAvatarLoading(false);
  }, []);

  return (
    <Box sx={{ position: "relative", display: "flex", justifyContent: "center" }}>
      <Box sx={{ position: "relative", width: "200px", height: "200px" }}>
        <Avatar
          src={avatarSrc}
          alt="Customer photo"
          sx={{ width: "100%", height: "100%" }}
          slotProps={{
            img: {
              onLoad: handleAvatarLoaded,
              onError: handleAvatarLoaded,
            },
          }}
        />

        {(isLoading || isAvatarLoading) && (
          <CircularProgress
            size="lg"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 2,
            }}
          />
        )}

        {!readOnly && (
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 10,
              display: "flex",
              gap: 1,
              zIndex: 3,
            }}
          >
            <label>
              <input
                type="file"
                accept=".jpg,.jpeg,image/jpeg"
                onChange={uploadImage}
                style={{ display: "none" }}
                disabled={isLoading}
              />
              <Tooltip title="Ajouter photo">
                <IconButtonCTA
                  ctaVariant="accent"
                  variant="plain"
                  aria-label="Ajouter photo client"
                  component="span"
                  disabled={isLoading}
                >
                  <SAddPhoto />
                </IconButtonCTA>
              </Tooltip>
            </label>
            {customer?.Photo && (
              <Tooltip title="Télécharger photo">
                <IconButtonCTA
                  ctaVariant="success"
                  variant="plain"
                  aria-label="Télécharger photo client"
                  onClick={downloadImage}
                  disabled={isLoading || isAvatarLoading}
                >
                  <SDownload />
                </IconButtonCTA>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
